import {
  EmailAuthProvider,
  reauthenticateWithCredential,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signOut as firebaseSignOut,
  updatePassword,
} from 'firebase/auth';
import { auth } from '../config/firebase';
import { postAdminPanelLogin, postAdminForgotPassword } from './admins';
import { postPartnerPanelLogin } from './partners';
import { clearPanelLoginCache } from './panelLoginCache';
import { teardownFcmWebRegistration } from './fcmWebRegistration';
import { USER_ROLES } from '../constants';

const ADMIN_ACCESS_DENIED_MESSAGE =
  'Access denied. This account is not authorized for the admin panel.';

const PARTNER_ACCESS_DENIED_MESSAGE = 'Access denied. Partner privileges required.';

const SESSION_ROLE_KEY = 'ss_session_role';

const mapFirebaseAuthErrorMessage = (error) => {
  const code = error?.code;

  switch (code) {
    case 'auth/invalid-credential':
    case 'auth/invalid-login-credentials':
    case 'auth/wrong-password':
    case 'auth/user-not-found':
      return 'Invalid email or password. Please try again.';
    case 'auth/invalid-email':
      return 'Please enter a valid email address.';
    case 'auth/user-disabled':
      return 'This account has been disabled. Please contact support.';
    case 'auth/too-many-requests':
      return 'Too many failed attempts. Please wait a moment and try again.';
    case 'auth/network-request-failed':
      return 'Network error. Please check your connection and try again.';
    default:
      return error?.message || 'Login failed. Please try again.';
  }
};

export const persistSessionRole = (role) => {
  if (role) {
    localStorage.setItem(SESSION_ROLE_KEY, role);
  } else {
    localStorage.removeItem(SESSION_ROLE_KEY);
  }
};

export const getPersistedSessionRole = () => localStorage.getItem(SESSION_ROLE_KEY);

/** Infer role from current route when localStorage has not been set yet. */
export const inferSessionRoleFromPath = (pathname = window.location.pathname) => {
  if (pathname.startsWith('/partner')) {
    return USER_ROLES.PARTNER;
  }
  return USER_ROLES.ADMIN;
};

export const getLoginPathForRole = (role) =>
  role === USER_ROLES.PARTNER ? '/partner-login' : '/login';

export const getLoginPathFromPathname = (pathname = window.location.pathname) =>
  getLoginPathForRole(inferSessionRoleFromPath(pathname));

export const getSessionRoleForRestore = (pathname = window.location.pathname) =>
  getPersistedSessionRole() || inferSessionRoleFromPath(pathname);

export const verifyAdminAccess = async ({ signOutOnFailure = true } = {}) => {
  const result = await postAdminPanelLogin();
  if (!result.success || !result.data?.isAdmin) {
    if (signOutOnFailure) {
      await firebaseSignOut(auth);
    }
    return {
      success: false,
      message: result.message || ADMIN_ACCESS_DENIED_MESSAGE,
    };
  }
  return {
    success: true,
    data: result.data,
  };
};

export const verifyPartnerAccess = async ({ signOutOnFailure = true } = {}) => {
  const result = await postPartnerPanelLogin();
  if (!result.success || !result.data?.isPartner) {
    if (signOutOnFailure) {
      await firebaseSignOut(auth);
    }
    return {
      success: false,
      message: result.message || PARTNER_ACCESS_DENIED_MESSAGE,
    };
  }
  return {
    success: true,
    data: result.data,
  };
};

export const shouldSignOutOnVerificationFailure = (result) => {
  if (result.success) return false;
  // API returned a logical denial (e.g. not admin) without throwing
  if (!result.error) return true;
  // Network / timeout — keep Firebase session so reload can retry
  if (!result.error.response) return false;
  const status = result.error.response?.status;
  return status === 401 || status === 403;
};

/**
 * Restore session after Firebase reload — calls exactly one backend login endpoint.
 */
export const resolveSessionRole = async (pathname = window.location.pathname) => {
  const role = getSessionRoleForRestore(pathname);

  if (role === USER_ROLES.PARTNER) {
    const partnerResult = await verifyPartnerAccess({ signOutOnFailure: false });
    if (partnerResult.success) {
      persistSessionRole(USER_ROLES.PARTNER);
      return { role: USER_ROLES.PARTNER, profile: partnerResult.data };
    }
    if (shouldSignOutOnVerificationFailure(partnerResult)) {
      await firebaseSignOut(auth);
      persistSessionRole(null);
    }
    return {
      role: null,
      profile: null,
      message: partnerResult.message,
      recoverable: !shouldSignOutOnVerificationFailure(partnerResult),
    };
  }

  const adminResult = await verifyAdminAccess({ signOutOnFailure: false });
  if (adminResult.success) {
    persistSessionRole(USER_ROLES.ADMIN);
    return { role: USER_ROLES.ADMIN, profile: adminResult.data };
  }

  if (shouldSignOutOnVerificationFailure(adminResult)) {
    await firebaseSignOut(auth);
    persistSessionRole(null);
  }

  return {
    role: null,
    profile: null,
    message: adminResult.message,
    recoverable: !shouldSignOutOnVerificationFailure(adminResult),
  };
};

export const firebaseEmailPasswordLogin = async (credentials) => {
  try {
    const email =
      credentials?.email ||
      (process.env.NODE_ENV === 'production' ? process.env.REACT_APP_ADMIN_EMAIL : '');
    const password =
      credentials?.password ||
      (process.env.NODE_ENV === 'production' ? process.env.REACT_APP_ADMIN_PASSWORD : '');

    await signInWithEmailAndPassword(auth, email, password);
    await auth.authStateReady();

    if (!auth.currentUser) {
      return {
        success: false,
        message: 'Login failed. Please try again.',
      };
    }

    return {
      success: true,
    };
  } catch (error) {
    return {
      success: false,
      message: mapFirebaseAuthErrorMessage(error),
    };
  }
};

export const adminLogin = firebaseEmailPasswordLogin;
export const partnerLogin = firebaseEmailPasswordLogin;

const mapFirebasePasswordResetErrorMessage = (error) => {
  const code = error?.code;

  switch (code) {
    case 'auth/invalid-email':
      return 'Please enter a valid email address.';
    case 'auth/missing-email':
      return 'Please enter your email address.';
    case 'auth/user-not-found':
      return 'No account found with this email address.';
    case 'auth/too-many-requests':
      return 'Too many requests. Please wait a moment and try again.';
    case 'auth/network-request-failed':
      return 'Network error. Please check your connection and try again.';
    default:
      return error?.message || 'Failed to send password reset email. Please try again.';
  }
};

export const mapFirebasePasswordUpdateErrorMessage = (error) => {
  const code = error?.code;

  switch (code) {
    case 'auth/invalid-credential':
    case 'auth/invalid-login-credentials':
    case 'auth/wrong-password':
      return 'Current password is incorrect.';
    case 'auth/weak-password':
      return 'Password is too weak. Choose a stronger password.';
    case 'auth/requires-recent-login':
      return 'Please sign in again, then try changing your password.';
    case 'auth/too-many-requests':
      return 'Too many attempts. Please wait a moment and try again.';
    case 'auth/network-request-failed':
      return 'Network error. Please check your connection and try again.';
    case 'auth/user-mismatch':
    case 'auth/user-not-found':
      return 'Session expired. Please sign in again.';
    default:
      return error?.message || 'Failed to update password. Please try again.';
  }
};

/**
 * Re-authenticate with the current password, then set a new Firebase password.
 * Partner (and admin) accounts use email/password via the shared Firebase auth instance.
 */
export const changeAuthenticatedUserPassword = async ({ currentPassword, newPassword }) => {
  await auth.authStateReady();
  const user = auth.currentUser;
  const email = user?.email?.trim();

  if (!user || !email) {
    return {
      success: false,
      message: 'You must be signed in to change your password.',
    };
  }

  try {
    const credential = EmailAuthProvider.credential(email, currentPassword);
    await reauthenticateWithCredential(user, credential);
    await updatePassword(user, newPassword);

    return {
      success: true,
      message: 'Password updated successfully.',
    };
  } catch (error) {
    const code = error?.code;
    const isWrongCurrentPassword =
      code === 'auth/wrong-password' ||
      code === 'auth/invalid-credential' ||
      code === 'auth/invalid-login-credentials';

    return {
      success: false,
      message: mapFirebasePasswordUpdateErrorMessage(error),
      fieldErrors: isWrongCurrentPassword
        ? { currentPassword: 'Current password is incorrect.' }
        : undefined,
    };
  }
};

export const sendPasswordReset = async (email, role = USER_ROLES.ADMIN) => {
  const trimmedEmail = String(email ?? '').trim();
  if (!trimmedEmail) {
    return {
      success: false,
      message: 'Please enter your email address.',
    };
  }

  const normalizedRole = role === USER_ROLES.PARTNER ? USER_ROLES.PARTNER : USER_ROLES.ADMIN;
  const verifyResult = await postAdminForgotPassword(trimmedEmail, normalizedRole);
  if (!verifyResult.success) {
    return verifyResult;
  }

  try {
    await sendPasswordResetEmail(auth, trimmedEmail);

    return {
      success: true,
      message: 'Password reset email sent. Check your inbox for further instructions.',
    };
  } catch (error) {
    return {
      success: false,
      message: mapFirebasePasswordResetErrorMessage(error),
    };
  }
};

export const adminLogout = async () => {
  clearPanelLoginCache();

  try {
    // Run before firebaseSignOut so unregister-device still has a Bearer token.
    await teardownFcmWebRegistration();
  } catch (error) {
    console.error('[FCM] Push teardown before sign-out failed:', error);
  }

  try {
    await firebaseSignOut(auth);
  } catch (error) {
    console.error('Error signing out of Firebase', error);
  }
};
